// Sends the signed-in user's Firebase ID token to the Cuelist backend
// (API_HOST: api.thecuelist.com / staging-api / localhost) on every request, so
// the backend can tell who is calling (backend step C, 13 Oct 2026; plan in
// CUELIST_BACKEND_AUTH_PLAN_2026-10-13.md).
//
// One place, three transports:
//   - vue-resource (Vue.http / this.$http): installApiAuth() adds an interceptor,
//     so every current and future call is covered with no call-site change.
//   - fetch / XMLHttpRequest (the page-upload modals): await apiAuthHeaders(url)
//     and pass the result as headers.
//
// No user, or the token cannot be fetched: the request goes WITHOUT the header
// rather than failing here. The backend decides (report mode logs it; enforce
// answers 401, which the callers already handle as a failed request).
//
// Pure module (no imports) so it can be unit tested under node; main.js passes
// in firebase and API_HOST.

export function isApiUrl(url, apiHost) {
    return !!apiHost && typeof url === 'string' && (url === apiHost || url.indexOf(apiHost + '/') === 0);
}

export function makeApiAuth({ getCurrentUser, apiHost }) {
    // {Authorization: 'Bearer <token>'} for an API_HOST url with a signed-in
    // user, {} otherwise. Never rejects. getIdToken() returns the cached token
    // and refreshes it only when it is near expiry.
    async function apiAuthHeaders(url) {
        if (url !== undefined && !isApiUrl(url, apiHost)) {
            return {};
        }
        try {
            const user = getCurrentUser();
            if (!user) {
                return {};
            }
            const token = await user.getIdToken();
            return token ? { Authorization: 'Bearer ' + token } : {};
        } catch (e) {
            // eslint-disable-next-line no-console
            console.warn('[api-auth] could not get an ID token; sending without one', e);
            return {};
        }
    }

    // vue-resource 1.x request interceptors are synchronous: one that returns an
    // object short-circuits the chain and that object becomes the response. So
    // the interceptor returns a promise that fetches the token, then re-issues
    // the same request (marked so it is not intercepted twice) through the full
    // chain. Ours runs first (custom interceptors run before the built-ins), so
    // nothing has touched the request yet.
    function interceptor(http) {
        return function apiAuthInterceptor(request) {
            if (request.apiAuthAttached || !isApiUrl(request.url, apiHost)) {
                return undefined;
            }
            return apiAuthHeaders().then((headers) => {
                Object.keys(headers).forEach(name => request.headers.set(name, headers[name]));
                return http(Object.assign({}, request, { apiAuthAttached: true }));
            });
        };
    }

    function installApiAuth(Vue) {
        Vue.http.interceptors.push(interceptor(Vue.http));
    }

    return { apiAuthHeaders, interceptor, installApiAuth };
}


// WEBPACK FOOTER //
// ./src/lib/api-auth.js